import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useAuth } from "react-oidc-context";
import {
  BriefcaseIcon,
  LeafIcon,
  GearIcon,
  ServiceSelectedCheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "../../icons";
import { ACTIVE_T3_SERVICES, ACTIVE_LANGUAGES, T3_DISCLAIMERS } from "../../constants";
import { FlagIcon } from "../../constants/languageFlags";
import VoiceSettingsModal from "../VoiceSettingsModal/VoiceSettingsModal";
import VoicePreferenceSummary from "../VoicePreferenceSummary/VoicePreferenceSummary";
import { fetchPollyVoices } from "../../services/pollyService";
import { useAuthorization } from "../../auth/useAuthorization";
import { PERMISSIONS } from "../../auth/permissions";
import { useVoicePreferences } from "../../auth/VoicePreferencesContext";
import "./ServiceSelection.scss";

const T3_SERVICES_WITH_ICONS = ACTIVE_T3_SERVICES.map((svc) => ({
  ...svc,
  icon: svc.id === "positioning" ? (s) => <BriefcaseIcon size={s} /> : (s) => <LeafIcon size={s} />,
}));

function ServiceSelection({ onNext, onBack, videoFileName: _videoFileName = "" }) {
  const auth = useAuth();
  const { hasPermission } = useAuthorization();
  const canManageVoices = hasPermission(PERMISSIONS.MANAGE_VOICE_PREFERENCES);
  const { pollyMapping, error: _voicePrefError, savePreferences } = useVoicePreferences();

  const userEmail = auth.user?.profile?.email || auth.user?.profile?.sub || "";

  const [selected, setSelected] = useState("");
  const [validationMsg, setValidationMsg] = useState("");
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [savedVoices, setSavedVoices] = useState({});
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [effectiveVoices, setEffectiveVoices] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [prefetchedVoices, setPrefetchedVoices] = useState({});
  const [pollyLoading, setPollyLoading] = useState(true);

  useEffect(() => {
    if (!canManageVoices) {
      return;
    }
    fetchPollyVoices(ACTIVE_LANGUAGES)
      .then((voices) => {
        setPrefetchedVoices(voices);
        setPollyLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[Polly] Prefetch failed:", err);
        setPollyLoading(false);
      });
  }, [canManageVoices]);

  // Languages for the currently-selected service (or all active if none chosen)
  const activeLanguages = selected
    ? T3_SERVICES_WITH_ICONS.find((s) => s.id === selected)?.languages || []
    : ACTIVE_LANGUAGES;

  const handleChange = (id) => {
    setSelected(id);
    setValidationMsg("");
  };

  const buildEffectiveVoices = useCallback(
    (languages) => {
      const result = {};
      languages.forEach((lang) => {
        if (savedVoices[lang]) {
          result[lang] = savedVoices[lang];
        } else {
          // Auto-assign first available Polly voice as default
          const available = prefetchedVoices[lang] || [];
          if (available.length > 0) result[lang] = { ...available[0], isDefault: true };
        }
      });
      return result;
    },
    [savedVoices, prefetchedVoices]
  );

  useEffect(() => {
    if (!summaryOpen || pollyLoading) return;
    const svc = T3_SERVICES_WITH_ICONS.find((s) => s.id === selected);
    const langs = svc?.languages || [];
    setEffectiveVoices(buildEffectiveVoices(langs));
  }, [prefetchedVoices, summaryOpen, pollyLoading, selected, buildEffectiveVoices]);

  const handleNext = () => {
    if (!selected) {
      setValidationMsg("Please select a service before continuing.");
      return;
    }
    const svc = T3_SERVICES_WITH_ICONS.find((s) => s.id === selected);
    const langs = svc?.languages || [];
    const voices = buildEffectiveVoices(langs);
    setEffectiveVoices(voices);

    // User role skips it entirely and uses Lambda-fetched pollyMapping.
    if (canManageVoices) {
      setSummaryOpen(true);
    } else {
      if (!pollyMapping || pollyMapping.length === 0) {
        setValidationMsg("Polly voice preferences not set by admin. Please contact your admin.");
        return;
      }
      // Reshape Lambda pollyMapping → voices map expected by upload flow
      const voicesFromLambda = {};
      pollyMapping.forEach((item) => {
        voicesFromLambda[item.language] = {
          name: item.voice_id,
          engine: item.engine,
          gender: item.gender,
        };
      });
      if (typeof onNext === "function") {
        onNext({
          serviceId: selected,
          serviceLabel: svc?.label || "",
          languages: langs,
          voices: voicesFromLambda,
        });
      }
    }
  };

  const handleConfirm = async () => {
    const svc = T3_SERVICES_WITH_ICONS.find((s) => s.id === selected);
    const langs = svc?.languages || [];

    // Reshape effectiveVoices map → pollyMapping array for Lambda
    const mappingToSave = langs
      .filter((lang) => effectiveVoices[lang])
      .map((lang) => ({
        language: lang,
        voice_id: effectiveVoices[lang].name?.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
        engine: effectiveVoices[lang].engine,
        gender: effectiveVoices[lang].gender,
      }));

    setIsSaving(true);
    setSaveError("");
    try {
      await savePreferences(userEmail, mappingToSave);
      setSummaryOpen(false);
      if (typeof onNext === "function") {
        onNext({
          serviceId: selected,
          serviceLabel: svc?.label || "",
          languages: langs,
          voices: effectiveVoices,
        });
      }
    } catch (err) {
      setSaveError(err.message || "Failed to save voice preferences. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleVoiceSave = (voices) => {
    setSavedVoices(voices);
  };

  return (
    <div className="tab-content">
      {/* ── Title row with settings button ───────────────────── */}
      <div className="ss-title-row">
        <h2 className="tab-title" style={{ margin: 0 }}>
          Select Service
        </h2>

        <div className="ss-title-actions">
          {/* Voice Settings button — Admin only (MANAGE_VOICE_PREFERENCES) */}
          {canManageVoices && (
            <button
              className="ss-settings-btn"
              onClick={() => setVoiceModalOpen(true)}
              aria-label="Open Voice Settings"
              title="Voice Settings"
            >
              <GearIcon size={16} />
              <span>Voice Settings</span>
            </button>
          )}
        </div>
      </div>

      <div className="ss-content-wrapper">
        <div className={`ss-disclaimer${selected ? " ss-disclaimer-selected" : ""}`}>
          <span className="ss-disclaimer-icon">ℹ️</span>
          <span className="ss-disclaimer-text">
            {selected ? T3_DISCLAIMERS[selected] : T3_DISCLAIMERS.default}
          </span>
        </div>

        <div className="ss-card-row">
          {T3_SERVICES_WITH_ICONS.map((svc) => {
            const active = selected === svc.id;
            const visibleFlags = svc.languages.slice(0, svc.maxFlags);
            const extra = svc.languages.length - svc.maxFlags;

            return (
              <div
                key={svc.id}
                className={`ss-card${active ? " ss-card-active" : ""}`}
                style={
                  active
                    ? {
                        borderColor: svc.accent,
                        boxShadow: `0 0 0 3px ${svc.accent}28`,
                      }
                    : {}
                }
                onClick={() => handleChange(svc.id)}
              >
                {/* Gradient Header */}
                <div className="ss-header" style={{ background: svc.gradient }}>
                  <div className="ss-radio-wrap">
                    <input
                      type="radio"
                      name="ss-service"
                      value={svc.id}
                      checked={active}
                      onChange={() => handleChange(svc.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="ss-radio"
                    />
                  </div>
                  <div className="ss-icon-circle">{svc.icon(36)}</div>
                  {/* Selected checkmark badge */}
                  {active && (
                    <div className="ss-selected-badge" aria-label="Selected">
                      <ServiceSelectedCheckIcon size={12} />
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="ss-body">
                  <div className="ss-label" style={{ color: active ? svc.accent : undefined }}>
                    {svc.label}
                  </div>
                  <div className="ss-lang-count">{svc.languages.length} languages</div>

                  <ul className="ss-bullets">
                    {svc.bullets.map((b, i) => (
                      <li key={i} className="ss-bullet">
                        <span className="ss-check" style={{ color: svc.checkColor }}>
                          ✓
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="ss-flags">
                    {visibleFlags.map((lang) => (
                      <span key={lang} className="ss-flag" data-tooltip={lang}>
                        <FlagIcon language={lang} size={22} />
                      </span>
                    ))}
                    {extra > 0 && <span className="ss-flag-extra">+{extra}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="ss-bottom-row">
        {onBack && (
          <button
            className="ss-back-btn"
            onClick={onBack}
            type="button"
            aria-label="Back to Dashboard"
          >
            <ArrowLeftIcon size={15} />
            Back
          </button>
        )}
        <button
          className={`save-btn ss-next-btn${!selected ? " ss-next-btn--disabled" : ""}`}
          onClick={handleNext}
          disabled={false}
        >
          <span>Next</span>
          <ArrowRightIcon size={16} />
        </button>
      </div>

      {validationMsg && (
        <div className="save-output save-output--error">
          <span className="save-output-icon save-output-icon--error">!</span>
          {validationMsg}
        </div>
      )}

      {/* ── Voice Settings Modal — Admin only (MANAGE_VOICE_PREFERENCES) ── */}
      {canManageVoices && (
        <VoiceSettingsModal
          open={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          languages={activeLanguages}
          selectedVoices={savedVoices}
          onSave={handleVoiceSave}
          prefetchedVoices={prefetchedVoices}
        />
      )}

      {/* ── Voice Preference Summary Modal — Admin only ── */}
      {canManageVoices && (
        <VoicePreferenceSummary
          open={summaryOpen}
          onClose={() => {
            setSummaryOpen(false);
            setSaveError("");
          }}
          onConfirm={handleConfirm}
          serviceLabel={T3_SERVICES_WITH_ICONS.find((s) => s.id === selected)?.label || ""}
          languages={T3_SERVICES_WITH_ICONS.find((s) => s.id === selected)?.languages || []}
          voices={effectiveVoices}
          loading={pollyLoading}
          isSaving={isSaving}
          saveError={saveError}
          onEditVoices={() => {
            setSummaryOpen(false);
            setVoiceModalOpen(true);
            setSaveError("");
          }}
        />
      )}
    </div>
  );
}

ServiceSelection.propTypes = {
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  videoFileName: PropTypes.string,
};

export default ServiceSelection;
